/**
 * FERP Portal - Entity Search Hook
 *
 * Repository-pattern hook for cross-subgraph entity search.
 * Debounces user input (300ms) before querying GraphQL.
 *
 * FERP Principles:
 * - Repository pattern: No direct fetch() calls
 * - Data-driven: Results come from backend SubgraphMetadata
 * - Dumb frontend: Hook fetches, component renders
 */

import { useLazyQuery } from '@apollo/client/react';
import { useDebouncedValue } from '@mantine/hooks';
import { useState, useEffect } from 'react';
import { SEARCH_ENTITIES } from '@/graphql/searchEntities';

export interface SearchResultItem {
  id: string;
  displayValue: string;
  entityType: string;
  subgraphId: string;
  identifiers: string | null;
  isLifeCritical: boolean;
  icon: string | null;
  detailPath: string | null;
}

export interface UseEntitySearchFilters {
  entityTypes?: string[];
  subgraphIds?: string[];
}

export interface UseEntitySearchResult {
  query: string;
  setQuery: (query: string) => void;
  debounced: string;
  results: SearchResultItem[];
  totalCount: number;
  loading: boolean;
  error: Error | null;
}

export function useEntitySearch(filters?: UseEntitySearchFilters): UseEntitySearchResult {
  const [query, setQuery] = useState('');
  const [debounced] = useDebouncedValue(query, 300);
  const [executeSearch, { data, loading, error }] = useLazyQuery<Record<string, any>>(SEARCH_ENTITIES);

  useEffect(() => {
    if (debounced.length >= 2) {
      executeSearch({
        variables: {
          input: {
            query: debounced,
            entityTypes: filters?.entityTypes,
            subgraphIds: filters?.subgraphIds,
            pagination: { limit: 20 },
          },
        },
      });
    }
  }, [debounced, filters, executeSearch]);

  return {
    query,
    setQuery,
    debounced,
    results: (data?.searchEntities?.items ?? []) as SearchResultItem[],
    totalCount: data?.searchEntities?.totalCount ?? 0,
    loading,
    error: error ?? null,
  };
}

export default useEntitySearch;
